import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Button,
  TablePagination,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as TableChartIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  Description as WordIcon,
  LocalOffer as LabelIcon,
  Assignment as ActIcon,
  AssignmentReturn as ReturnActIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { filterArtworksBySearch } from '../utils/searchNormalize';
import CollectionSelect from '../components/CollectionSelect';

const TAB_CATALOG = 'catalog';
const TAB_SIMPLE = 'simple';
const TAB_LABELS = 'labels';
const TAB_ACTS = 'acts';

const STORAGE_LISTS_TAB = 'lists_tab';

const EMPTY_ACT_FORM = {
  exhibition_name: '',
  provision_date_start: '',
  provision_date_end: '',
  exhibition_date_start: '',
  exhibition_date_end: '',
  exhibition_city: '',
  sender_org: '',
  sender_position: '',
  sender_name: '',
  receiver_org: '',
  receiver_position: '',
  receiver_name: '',
};

function getInitialTab() {
  try {
    const v = sessionStorage.getItem(STORAGE_LISTS_TAB);
    return v === TAB_SIMPLE || v === TAB_CATALOG || v === TAB_LABELS || v === TAB_ACTS
      ? v
      : TAB_CATALOG;
  } catch {
    return TAB_CATALOG;
  }
}

function buildLabelText(artwork) {
  const lines = [];
  if (artwork.author?.full_name) lines.push(artwork.author.full_name);
  if (artwork.artwork_title) lines.push(`«${artwork.artwork_title}»`);

  const placeYear = [];
  const place = (artwork.storage_place || '').trim();
  if (place) placeYear.push(place);
  if (artwork.creation_year_display) {
    placeYear.push(String(artwork.creation_year_display));
  } else if (artwork.creation_year != null && artwork.creation_year !== '') {
    placeYear.push(String(artwork.creation_year));
  }
  if (placeYear.length) lines.push(placeYear.join(', '));

  const material = (artwork.material_and_technique || artwork.material || artwork.technique || '').trim();
  if (material) lines.push(material);

  const dims = [];
  if (artwork.height_cm != null && artwork.height_cm !== '') dims.push(String(artwork.height_cm));
  if (artwork.width_cm != null && artwork.width_cm !== '') dims.push(String(artwork.width_cm));
  if (artwork.depth_cm != null && artwork.depth_cm !== '') dims.push(String(artwork.depth_cm));
  if (dims.length) lines.push(`${dims.join(' × ')} см`);

  return lines.join('\n');
}

/** Текст печатной этикетки (как в PDF): автор, название, шифр, место/год, техника. */
function buildPrintLabelText(artwork) {
  const lines = [];
  lines.push(artwork.author?.full_name || 'Неизвестный автор');
  lines.push((artwork.artwork_title || '').trim() || '—');
  lines.push((artwork.inventory_number || '').trim() || '—');

  const placeYear = [];
  const place = (artwork.storage_place || '').trim();
  if (place) placeYear.push(place);
  if (artwork.creation_year_display) {
    placeYear.push(String(artwork.creation_year_display));
  } else if (artwork.creation_year != null && artwork.creation_year !== '') {
    placeYear.push(String(artwork.creation_year));
  }
  lines.push(placeYear.length ? placeYear.join(', ') : '—');

  const material = (artwork.material_and_technique || '').trim();
  lines.push(material || '—');

  return lines.join('\n');
}

function objectsWordRu(n) {
  const abs = Math.abs(n) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return 'объектов';
  if (d === 1) return 'объект';
  if (d >= 2 && d <= 4) return 'объекта';
  return 'объектов';
}

/** Пока сервер не отдаёт байты ответа, реального % нет — плавная оценка по времени (асимптота к 95%). */
function pseudoServerFormationPercent(elapsedSec) {
  const t = Math.max(0, elapsedSec);
  if (t === 0) return 1;
  const raw = 100 * (1 - Math.exp(-t / 42));
  return Math.min(95, Math.max(1, Math.floor(raw)));
}

export default function Lists() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(getInitialTab);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('inventory_number');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(TAB_CATALOG);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [catalogExportProgress, setCatalogExportProgress] = useState(null);
  const [catalogExportWaitingServer, setCatalogExportWaitingServer] = useState(false);
  const [catalogExportItemCount, setCatalogExportItemCount] = useState(0);
  const [catalogExportElapsedSec, setCatalogExportElapsedSec] = useState(0);
  const catalogPdfResponseDoneRef = useRef(false);
  const catalogExportTimerRef = useRef(null);

  const [actDialogOpen, setActDialogOpen] = useState(false);
  const [actDialogType, setActDialogType] = useState('выдача');
  const [actForm, setActForm] = useState(EMPTY_ACT_FORM);
  const [actLoading, setActLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(
    () => () => {
      if (catalogExportTimerRef.current != null) {
        window.clearInterval(catalogExportTimerRef.current);
        catalogExportTimerRef.current = null;
      }
    },
    []
  );

  const { data, isLoading } = useQuery(
    ['lists_artworks'],
    async () => {
      const response = await axios.get('/api/artworks', {
        params: { per_page: 10000 },
      });
      return response.data?.artworks || [];
    },
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const artworks = data || [];

  const filteredSortedArtworks = useMemo(() => {
    let res = artworks;
    if (collectionFilter) {
      res = res.filter((a) => a.collection_code === collectionFilter);
    }
    if (tab === TAB_ACTS && statusFilter) {
      res = res.filter((a) => a.status === statusFilter);
    }

    const query = search.trim();
    if (query) {
      return filterArtworksBySearch(res, query, (a) => [
        a.artwork_title,
        a.artwork_description,
        a.material_and_technique,
        a.inventory_number,
        a.collection_code,
        a.storage_place,
        a.creation_year_display,
        a.creation_year_text,
        a.creation_year,
        ...(a.tags || []).map((t) => t.name),
        a.author?.full_name,
        a.author?.last_name,
      ]);
    }

    res = [...res].sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      const av = (a[sortField] ?? '').toString().toLowerCase();
      const bv = (b[sortField] ?? '').toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return res;
  }, [artworks, search, collectionFilter, statusFilter, tab, sortField, sortDirection]);

  const handleSearchClick = () => {
    setSearch(searchInput);
    setPage(0);
  };

  const toggleSelectId = useCallback((id) => {
    if (id == null) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    setSelectedIds(new Set(filteredSortedArtworks.map((a) => a.id).filter(Boolean)));
  }, [filteredSortedArtworks]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const artworksForExport = useMemo(() => {
    // Выбранные сохраняются при смене поиска — берём из полного списка
    const selected = artworks.filter((a) => a.id != null && selectedIds.has(a.id));
    return selected.length > 0 ? selected : filteredSortedArtworks;
  }, [artworks, filteredSortedArtworks, selectedIds]);

  const pageSlice = useMemo(
    () =>
      filteredSortedArtworks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSortedArtworks, page, rowsPerPage]
  );

  const allPageSelected =
    pageSlice.length > 0 && pageSlice.every((a) => a.id != null && selectedIds.has(a.id));

  const toggleSelectAllPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageSlice.forEach((a) => {
          if (a.id != null) next.delete(a.id);
        });
      } else {
        pageSlice.forEach((a) => {
          if (a.id != null) next.add(a.id);
        });
      }
      return next;
    });
  }, [allPageSelected, pageSlice]);

  const selectedArtworks = useMemo(() => {
    const byId = new Map(artworks.map((a) => [a.id, a]));
    return [...selectedIds]
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [artworks, selectedIds]);

  const selectedCount = selectedArtworks.length;

  const selectedCountInView = useMemo(
    () => filteredSortedArtworks.filter((a) => a.id != null && selectedIds.has(a.id)).length,
    [filteredSortedArtworks, selectedIds]
  );

  const actSelectionRules = useMemo(() => {
    if (selectedArtworks.length === 0) {
      return {
        canTransfer: false,
        canReturn: false,
        hint: 'Для создания акта отметьте произведения в списке',
      };
    }
    const statuses = new Set(selectedArtworks.map((a) => a.status));
    const onlyStorage = statuses.size === 1 && statuses.has('на хранении');
    const onlyExposition = statuses.size === 1 && statuses.has('в экспозиции');
    if (onlyStorage) {
      return {
        canTransfer: true,
        canReturn: false,
        hint: 'Выбраны произведения на хранении — доступен акт приёма-передачи',
      };
    }
    if (onlyExposition) {
      return {
        canTransfer: false,
        canReturn: true,
        hint: 'Выбраны произведения в экспозиции — доступен акт возврата',
      };
    }
    return {
      canTransfer: false,
      canReturn: false,
      hint: 'Нельзя создать акт: в выборе смешаны статусы «на хранении» и «в экспозиции» (или другие). Выберите произведения одного статуса.',
    };
  }, [selectedArtworks]);

  const somePageSelected = useMemo(
    () => pageSlice.some((a) => a.id != null && selectedIds.has(a.id)),
    [pageSlice, selectedIds]
  );

  const pageCheckboxIndeterminate = somePageSelected && !allPageSelected;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTab = (event, value) => {
    if (!value) return;
    setTab(value);
    try {
      sessionStorage.setItem(STORAGE_LISTS_TAB, value);
    } catch {
      // ignore
    }
  };

  const handleRequestSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportSimpleExcel = async () => {
    if (!artworksForExport.length) return;
    const ids = artworksForExport.map((a) => a.id).filter((id) => id != null);
    if (!ids.length) {
      window.alert('Нет произведений для экспорта');
      return;
    }
    try {
      const response = await axios.post(
        '/api/artworks/simple-list-xlsx',
        { ids },
        { responseType: 'blob' }
      );
      const blob = response.data;
      if (!blob || blob.size === 0) {
        throw new Error('Пустой файл');
      }
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'spisok.xlsx';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Ошибка экспорта Excel:', e);
      let msg = 'Не удалось скачать Excel';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          if (j.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      window.alert(msg);
    }
  };

  const handleExportSimplePdf = async () => {
    if (!artworksForExport.length) return;
    const ids = artworksForExport.map((a) => a.id).filter((id) => id != null);
    if (!ids.length) {
      window.alert('Нет произведений для экспорта');
      return;
    }
    try {
      const response = await axios.post(
        '/api/artworks/simple-list-pdf',
        { ids },
        { responseType: 'blob' }
      );
      const blob = response.data;
      if (!blob || blob.size === 0) {
        throw new Error('Пустой файл');
      }
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'spisok.pdf';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Ошибка экспорта PDF:', e);
      let msg = 'Не удалось скачать PDF';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          if (j.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      window.alert(msg);
    }
  };

  const handleExportSimpleWord = async () => {
    if (!artworksForExport.length) return;
    const ids = artworksForExport.map((a) => a.id).filter((id) => id != null);
    if (!ids.length) {
      window.alert('Нет произведений для экспорта');
      return;
    }
    try {
      const response = await axios.post(
        '/api/artworks/simple-list-docx',
        { ids },
        { responseType: 'blob' }
      );
      const blob = response.data;
      if (!blob || blob.size === 0) {
        throw new Error('Пустой файл');
      }
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'spisok.docx';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Ошибка экспорта Word:', e);
      let msg = 'Не удалось скачать Word';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          if (j.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      window.alert(msg);
    }
  };

  const handleExportLabelsPdf = async () => {
    if (!artworksForExport.length) return;
    const ids = artworksForExport.map((a) => a.id).filter((id) => id != null);
    if (!ids.length) {
      window.alert('Нет произведений для этикеток');
      return;
    }
    try {
      const response = await axios.post(
        '/api/artworks/labels-pdf',
        { ids },
        { responseType: 'blob' }
      );
      const blob = response.data;
      if (!blob || blob.size === 0) {
        throw new Error('Пустой файл');
      }
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'etiketki.pdf';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error('Ошибка генерации этикеток:', e);
      let msg = 'Не удалось скачать этикетки';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          if (j.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      window.alert(msg);
    }
  };

  const handleExportCatalogPdf = async () => {
    if (!artworksForExport.length) return;
    const ids = artworksForExport.map((a) => a.id).filter((id) => id != null);
    if (!ids.length) {
      return;
    }

    catalogPdfResponseDoneRef.current = false;
    if (catalogExportTimerRef.current != null) {
      window.clearInterval(catalogExportTimerRef.current);
      catalogExportTimerRef.current = null;
    }
    setCatalogExportItemCount(ids.length);
    setCatalogExportElapsedSec(0);
    catalogExportTimerRef.current = window.setInterval(() => {
      setCatalogExportElapsedSec((s) => s + 1);
    }, 1000);
    setCatalogExportWaitingServer(true);
    setCatalogExportProgress(0);
    try {
      const response = await axios.post(
        '/api/artworks/catalog-pdf',
        { ids },
        {
          responseType: 'blob',
          onDownloadProgress: (ev) => {
            if (catalogPdfResponseDoneRef.current) return;
            if (ev.total) {
              if (ev.loaded === 0) return;
              if (catalogExportTimerRef.current != null) {
                window.clearInterval(catalogExportTimerRef.current);
                catalogExportTimerRef.current = null;
              }
              setCatalogExportElapsedSec(0);
              const pct = Math.ceil((ev.loaded / ev.total) * 100);
              setCatalogExportWaitingServer(false);
              setCatalogExportProgress(Math.min(99, Math.max(1, pct)));
            } else if (ev.loaded > 0) {
              if (catalogExportTimerRef.current != null) {
                window.clearInterval(catalogExportTimerRef.current);
                catalogExportTimerRef.current = null;
              }
              setCatalogExportElapsedSec(0);
              setCatalogExportWaitingServer(false);
              const approx = Math.min(94, 5 + Math.floor(ev.loaded / (512 * 1024)));
              setCatalogExportProgress(approx);
            }
          },
        }
      );
      catalogPdfResponseDoneRef.current = true;
      const blob = response.data;
      if (!blob || blob.size === 0) {
        throw new Error('Пустой файл');
      }
      if (catalogExportTimerRef.current != null) {
        window.clearInterval(catalogExportTimerRef.current);
        catalogExportTimerRef.current = null;
      }
      setCatalogExportElapsedSec(0);
      setCatalogExportWaitingServer(false);
      setCatalogExportProgress(100);
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'catalog.pdf';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      if (catalogExportTimerRef.current != null) {
        window.clearInterval(catalogExportTimerRef.current);
        catalogExportTimerRef.current = null;
      }
      setCatalogExportElapsedSec(0);
      console.error('Ошибка экспорта каталога:', e);
      let msg = 'Не удалось скачать каталог';
      if (e.response?.data instanceof Blob) {
        try {
          const t = await e.response.data.text();
          const j = JSON.parse(t);
          if (j.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      window.alert(msg);
    } finally {
      if (catalogExportTimerRef.current != null) {
        window.clearInterval(catalogExportTimerRef.current);
        catalogExportTimerRef.current = null;
      }
      setCatalogExportElapsedSec(0);
      setCatalogExportItemCount(0);
      window.setTimeout(() => {
        setCatalogExportProgress(null);
        setCatalogExportWaitingServer(false);
      }, 2800);
    }
  };

  const handleOpenPreview = (mode) => {
    setPreviewMode(mode);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const openActDialog = (type) => {
    if (selectedCount === 0) {
      showSnackbar('Выберите хотя бы одно произведение', 'error');
      return;
    }
    if (type === 'выдача' && !actSelectionRules.canTransfer) {
      showSnackbar(
        'Акт приёма-передачи доступен только если все выбранные произведения имеют статус «на хранении»',
        'error'
      );
      return;
    }
    if (type === 'возврат' && !actSelectionRules.canReturn) {
      showSnackbar(
        'Акт возврата доступен только если все выбранные произведения имеют статус «в экспозиции»',
        'error'
      );
      return;
    }
    setActDialogType(type);
    setActForm(EMPTY_ACT_FORM);
    setActDialogOpen(true);
  };

  const handleActFormChange = (field) => (event) => {
    setActForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmitAct = async () => {
    if (!actForm.exhibition_name.trim()) {
      showSnackbar('Укажите наименование мероприятия', 'error');
      return;
    }
    if (!actForm.exhibition_city.trim()) {
      showSnackbar('Укажите место проведения выставки', 'error');
      return;
    }

    const ids = selectedArtworks.map((a) => a.id).filter(Boolean);
    if (ids.length === 0) {
      showSnackbar('Выберите хотя бы одно произведение', 'error');
      return;
    }

    setActLoading(true);
    try {
      const response = await axios.post(
        '/api/artworks/internal-acts/bulk',
        {
          ids,
          act_type: actDialogType,
          ...actForm,
        },
        { responseType: 'blob' }
      );

      if (response.data?.type === 'application/json') {
        const text = await response.data.text();
        let message = 'Ошибка генерации акта';
        try {
          message = JSON.parse(text).error || message;
        } catch (_) {
          /* ignore */
        }
        throw new Error(message);
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      link.setAttribute('download', match?.[1] || 'act.docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setActDialogOpen(false);
      clearSelection();
      queryClient.invalidateQueries('lists_artworks');
      queryClient.invalidateQueries('artworks');
      queryClient.invalidateQueries('statistics');
      queryClient.invalidateQueries('archive');
      showSnackbar(
        actDialogType === 'возврат'
          ? 'Акт возврата создан. Статус произведений: на хранении'
          : 'Акт приёма-передачи создан. Статус произведений: в экспозиции',
        'success'
      );
    } catch (err) {
      let msg = err.message || 'Ошибка генерации акта';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error) msg = parsed.error;
        } catch (_) {
          /* ignore */
        }
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      showSnackbar(msg, 'error');
    } finally {
      setActLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Списки
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Каталог" value={TAB_CATALOG} />
          <Tab label="Список" value={TAB_SIMPLE} />
          <Tab label="Этикетки" value={TAB_LABELS} />
          <Tab label="Акты" value={TAB_ACTS} />
        </Tabs>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={tab === TAB_ACTS ? 3 : 4}>
          <Box display="flex" gap={1} alignItems="flex-start">
            <TextField
              fullWidth
              label="Поиск"
              variant="outlined"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
              placeholder="Поиск… Слова через +, теги с # (напр. #масло)"
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearchClick}
              sx={{ minWidth: 100, flexShrink: 0 }}
            >
              Поиск
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={tab === TAB_ACTS ? 3 : 4}>
          <CollectionSelect
            allowEmpty
            value={collectionFilter}
            onChange={(e) => setCollectionFilter(e.target.value)}
          />
        </Grid>
        {tab === TAB_ACTS && (
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Статус</InputLabel>
              <Select
                value={statusFilter}
                label="Статус"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">Все</MenuItem>
                <MenuItem value="на хранении">На хранении</MenuItem>
                <MenuItem value="в экспозиции">В экспозиции</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} md={tab === TAB_ACTS ? 3 : 4}>
          <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
            {tab === TAB_ACTS && (
              <>
                <Button
                  variant="contained"
                  startIcon={<ActIcon />}
                  onClick={() => openActDialog('выдача')}
                  disabled={!actSelectionRules.canTransfer || actLoading}
                >
                  Создать акт приёма-передачи
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ReturnActIcon />}
                  onClick={() => openActDialog('возврат')}
                  disabled={!actSelectionRules.canReturn || actLoading}
                >
                  Акт возврата
                </Button>
              </>
            )}
            {tab === TAB_SIMPLE && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => handleOpenPreview(TAB_SIMPLE)}
                >
                  Предпросмотр
                </Button>
                <Button
                  variant="contained"
                  startIcon={<TableChartIcon />}
                  onClick={handleExportSimpleExcel}
                >
                  Экспорт в Excel
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PdfIcon />}
                  onClick={handleExportSimplePdf}
                >
                  Экспорт в PDF
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<WordIcon />}
                  onClick={handleExportSimpleWord}
                >
                  Экспорт в Word
                </Button>
              </>
            )}
            {tab === TAB_LABELS && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => handleOpenPreview(TAB_LABELS)}
                >
                  Предпросмотр
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<LabelIcon />}
                  onClick={handleExportLabelsPdf}
                >
                  Сгенерировать этикетки (PDF)
                </Button>
              </>
            )}
            {tab === TAB_CATALOG && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => handleOpenPreview(TAB_CATALOG)}
                  disabled={catalogExportProgress !== null}
                >
                  Предпросмотр
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<PdfIcon />}
                    onClick={handleExportCatalogPdf}
                    disabled={catalogExportProgress !== null}
                  >
                    Экспорт каталога (PDF)
                  </Button>
                  {catalogExportProgress !== null && (
                    <Box sx={{ minWidth: { xs: '100%', sm: 160 }, maxWidth: 280, flex: '1 1 auto' }}>
                      <Tooltip
                        title="Пока сервер не начал отдавать файл, точный процент неизвестен. До этого показывается оценка по времени ожидания."
                        arrow
                        placement="top"
                      >
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {catalogExportWaitingServer && catalogExportProgress === 0
                            ? `Генерация каталога: ${pseudoServerFormationPercent(
                                catalogExportElapsedSec
                              )}% (оценка) · ${catalogExportItemCount} ${objectsWordRu(
                                catalogExportItemCount
                              )} · сервер · ${catalogExportElapsedSec} с`
                            : `Генерация каталога: ${catalogExportProgress}%`}
                        </Typography>
                      </Tooltip>
                      <LinearProgress
                        variant="determinate"
                        value={
                          catalogExportWaitingServer && catalogExportProgress === 0
                            ? pseudoServerFormationPercent(catalogExportElapsedSec)
                            : catalogExportProgress
                        }
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Выбрано: {selectedCount}
            {filteredSortedArtworks.length > 0
              ? ` · в списке: ${filteredSortedArtworks.length}`
              : ''}
            {search.trim() && selectedCount > selectedCountInView
              ? ` · скрыто поиском: ${selectedCount - selectedCountInView}`
              : ''}
          </Typography>
          <Button size="small" variant="outlined" onClick={selectAllFiltered}>
            Выбрать все в списке
          </Button>
          <Button size="small" variant="outlined" onClick={clearSelection} disabled={selectedCount === 0}>
            Снять выделение
          </Button>
          {(tab === TAB_CATALOG || tab === TAB_SIMPLE || tab === TAB_LABELS) && (
            <Tooltip title="Если ничего не отмечено, экспорт включает весь отфильтрованный список. Выбранные сохраняются при смене поиска.">
              <Typography variant="caption" color="text.secondary" sx={{ ml: { xs: 0, sm: 1 } }}>
                Экспорт использует отмеченные позиции
              </Typography>
            </Tooltip>
          )}
          {tab === TAB_ACTS && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: { xs: 0, sm: 1 } }}>
              {actSelectionRules.hint}
            </Typography>
          )}
        </Box>
        {selectedArtworks.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              gap: 0.75,
              ml: 'auto',
              minWidth: 0,
              overflowX: 'auto',
              py: 0.25,
              pl: 1,
            }}
          >
            {selectedArtworks.map((a) => (
              <Chip
                key={a.id}
                size="small"
                label={a.inventory_number || a.artwork_title || `#${a.id}`}
                onDelete={() => toggleSelectId(a.id)}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}
      </Box>

      {(tab === TAB_SIMPLE || tab === TAB_LABELS || tab === TAB_ACTS) && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={pageCheckboxIndeterminate}
                    checked={allPageSelected && pageSlice.length > 0}
                    onChange={toggleSelectAllPage}
                    inputProps={{ 'aria-label': 'Выбрать все на странице' }}
                  />
                </TableCell>
                <TableCell>№</TableCell>
                <TableCell>Изображение</TableCell>
                <TableCell sortDirection={sortField === 'artwork_title' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'artwork_title'}
                    direction={sortField === 'artwork_title' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('artwork_title')}
                  >
                    Наименование
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'inventory_number' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'inventory_number'}
                    direction={sortField === 'inventory_number' ? sortDirection : 'asc'}
                    onClick={() => handleRequestSort('inventory_number')}
                  >
                    ШИФР
                  </TableSortLabel>
                </TableCell>
                {tab === TAB_ACTS && (
                  <TableCell sortDirection={sortField === 'status' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >
                      Статус
                    </TableSortLabel>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSortedArtworks
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((a, index) => (
                <TableRow key={a.id || index} hover selected={a.id != null && selectedIds.has(a.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={a.id != null && selectedIds.has(a.id)}
                      onChange={() => toggleSelectId(a.id)}
                      disabled={a.id == null}
                      inputProps={{ 'aria-label': `Выбрать ${a.artwork_title || a.inventory_number || ''}` }}
                    />
                  </TableCell>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    {a.primary_image?.file_path ? (
                      <img
                        src={`/uploads/${a.primary_image.file_path}`}
                        alt={a.artwork_title}
                        style={{ width: 60, height: 60, objectFit: 'cover' }}
                        loading="lazy"
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>{buildLabelText(a)}</TableCell>
                  <TableCell>{a.inventory_number}</TableCell>
                  {tab === TAB_ACTS && <TableCell>{a.status || '—'}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(tab === TAB_SIMPLE || tab === TAB_LABELS || tab === TAB_ACTS) && (
        <TablePagination
          component="div"
          count={filteredSortedArtworks.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          labelRowsPerPage="Записей на странице:"
        />
      )}

      {tab === TAB_CATALOG && (
        <Grid container spacing={2}>
          {filteredSortedArtworks.map((a) => (
            <Grid item xs={12} md={6} lg={4} key={a.id}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: 400,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 2,
                  position: 'relative',
                }}
              >
                <Checkbox
                  sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1, bgcolor: 'background.paper', borderRadius: 0.5 }}
                  size="small"
                  checked={a.id != null && selectedIds.has(a.id)}
                  onChange={() => toggleSelectId(a.id)}
                  disabled={a.id == null}
                  inputProps={{ 'aria-label': `Выбрать ${a.artwork_title || a.inventory_number || ''}` }}
                />
                <Box
                  sx={{
                    flex: 1,
                    border: '1px dashed #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1.5,
                  }}
                >
                  {a.primary_image?.file_path ? (
                    <img
                      src={`/uploads/${a.primary_image.file_path}`}
                      alt={a.artwork_title}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                      loading="lazy"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Нет изображения
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      border: '1px solid #ccc',
                      p: 1,
                      maxWidth: '100%',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {buildLabelText(a)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
          {filteredSortedArtworks.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary">
                Нет произведений для отображения.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth={previewMode === TAB_SIMPLE || previewMode === TAB_LABELS ? 'lg' : 'md'}
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {previewMode === TAB_SIMPLE
              ? 'Предпросмотр списка'
              : previewMode === TAB_LABELS
                ? 'Предпросмотр этикеток'
                : 'Предпросмотр каталога'}
          </Typography>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewMode === TAB_SIMPLE && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>Изображение</TableCell>
                  <TableCell>Наименование</TableCell>
                  <TableCell>ШИФР</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {artworksForExport.slice(0, 20).map((a, index) => (
                  <TableRow key={a.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {a.primary_image?.file_path ? (
                        <img
                          src={`/uploads/${a.primary_image.file_path}`}
                          alt={a.artwork_title}
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-line' }}>{buildLabelText(a)}</TableCell>
                    <TableCell>{a.inventory_number}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {previewMode === TAB_LABELS && (
            <Grid container spacing={2}>
              {artworksForExport.slice(0, 6).map((a, index) => {
                const labelLines = buildPrintLabelText(a).split('\n');
                const [authorLine, titleLine, cipherLine, ...restLines] = labelLines;
                return (
                <Grid item xs={12} md={6} key={a.id || index}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }}
                  >
                    {a.primary_image?.file_path ? (
                      <img
                        src={`/uploads/${a.primary_image.file_path}`}
                        alt={a.artwork_title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: 160,
                          objectFit: 'contain',
                          display: 'block',
                          marginBottom: 8,
                        }}
                      />
                    ) : null}
                    <Typography variant="subtitle2" fontWeight="bold">
                      {authorLine}
                    </Typography>
                    <Typography variant="body2">{titleLine}</Typography>
                    <Typography variant="body2">{cipherLine}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {restLines.join('\n')}
                    </Typography>
                  </Paper>
                </Grid>
                );
              })}
              {artworksForExport.length === 0 && (
                <Grid item xs={12}>
                  <Typography color="text.secondary">Нет произведений для отображения.</Typography>
                </Grid>
              )}
              {artworksForExport.length > 6 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Показаны первые 6 из {artworksForExport.length}. В PDF — до 6 этикеток на лист A4.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}

          {previewMode === TAB_CATALOG && (
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: 1,
                p: 2,
                height: 500,
                display: 'flex',
              }}
            >
              {artworksForExport[0] ? (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      mr: 2,
                      border: '1px dashed #ccc',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {artworksForExport[0].primary_image?.file_path ? (
                      <img
                        src={`/uploads/${artworksForExport[0].primary_image.file_path}`}
                        alt={artworksForExport[0].artwork_title}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        loading="lazy"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Нет изображения
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'flex-end',
                      p: 1.5,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        border: '1px solid #ccc',
                        p: 2,
                        maxWidth: '100%',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {buildLabelText(artworksForExport[0])}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ m: 'auto' }}>
                  Нет произведений для отображения.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={actDialogOpen}
        onClose={() => !actLoading && setActDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {actDialogType === 'возврат'
            ? 'Акт возврата'
            : 'Акт приёма-передачи'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Выбрано произведений: {selectedCount}. После сохранения
            {actDialogType === 'возврат'
              ? ' статус станет «на хранении»'
              : ' статус станет «в экспозиции»'}
            , в карточки будет добавлена запись о выставке/ярмарке.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Наименование мероприятия"
              value={actForm.exhibition_name}
              onChange={handleActFormChange('exhibition_name')}
              required
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Дата предоставления от"
                  placeholder="ДД.ММ.ГГГГ"
                  value={actForm.provision_date_start}
                  onChange={handleActFormChange('provision_date_start')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Дата предоставления до"
                  placeholder="ДД.ММ.ГГГГ"
                  value={actForm.provision_date_end}
                  onChange={handleActFormChange('provision_date_end')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Дата экспонирования от"
                  placeholder="ДД.ММ.ГГГГ"
                  value={actForm.exhibition_date_start}
                  onChange={handleActFormChange('exhibition_date_start')}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Дата экспонирования до"
                  placeholder="ДД.ММ.ГГГГ"
                  value={actForm.exhibition_date_end}
                  onChange={handleActFormChange('exhibition_date_end')}
                  fullWidth
                />
              </Grid>
            </Grid>
            <TextField
              label="Место проведения выставки"
              value={actForm.exhibition_city}
              onChange={handleActFormChange('exhibition_city')}
              required
              fullWidth
            />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Направляющая сторона
            </Typography>
            <TextField
              label="Наименование учреждения (направляющая сторона)"
              value={actForm.sender_org}
              onChange={handleActFormChange('sender_org')}
              fullWidth
            />
            <TextField
              label="Должность ответственного сотрудника направляющей стороны"
              value={actForm.sender_position}
              onChange={handleActFormChange('sender_position')}
              fullWidth
            />
            <TextField
              label="ФИО человека направляющей стороны"
              value={actForm.sender_name}
              onChange={handleActFormChange('sender_name')}
              fullWidth
            />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Принимающая сторона
            </Typography>
            <TextField
              label="Наименование учреждения (принимающая сторона)"
              value={actForm.receiver_org}
              onChange={handleActFormChange('receiver_org')}
              fullWidth
            />
            <TextField
              label="Должность ответственного сотрудника принимающей стороны"
              value={actForm.receiver_position}
              onChange={handleActFormChange('receiver_position')}
              fullWidth
            />
            <TextField
              label="ФИО человека принимающей стороны"
              value={actForm.receiver_name}
              onChange={handleActFormChange('receiver_name')}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActDialogOpen(false)} disabled={actLoading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAct}
            disabled={actLoading}
            startIcon={actLoading ? <CircularProgress size={18} color="inherit" /> : <ActIcon />}
          >
            {actLoading ? 'Формирование…' : 'Создать акт'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

